import { useState } from "react";
import { Trophy, Weight } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTranslation } from "@/hooks/useTranslation";
import type { StrengthProgressPoint } from "../types";

interface StrengthExerciseOverviewProps {
  exerciseName: string;
  points: StrengthProgressPoint[];
}

type Metric = "estimatedOneRepMaxKg" | "maxWeightKg" | "volumeKg";
const metrics: Record<Metric, { key: string; color: string }> = {
  estimatedOneRepMaxKg: { key: "estimatedOneRepMax", color: "#2563eb" },
  maxWeightKg: { key: "maxWeight", color: "#16a34a" },
  volumeKg: { key: "volume", color: "#9333ea" },
};

export function StrengthExerciseOverview({
  exerciseName,
  points,
}: StrengthExerciseOverviewProps) {
  const { t, i18n } = useTranslation();
  const [metric, setMetric] = useState<Metric>("estimatedOneRepMaxKg");
  const latest = points.at(-1);
  if (!latest) return null;
  const best = Math.max(...points.map(point => point.estimatedOneRepMaxKg));
  const formatNumber = (value: number) =>
    value.toLocaleString(i18n.language, { maximumFractionDigits: 1 });
  const formatDate = (date: string) =>
    new Intl.DateTimeFormat(i18n.language, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(`${date}T00:00:00`));

  return (
    <section className="space-y-4" aria-label={exerciseName}>
      <div>
        <h2 className="text-xl font-semibold">{exerciseName}</h2>
        <p className="text-sm text-muted-foreground">
          {t("strengthProgress:summary.latestTraining")} ·{" "}
          {formatDate(latest.date)}
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("strengthProgress:summary.latestLoad")}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-3">
            <Weight className="h-5 w-5 text-green-600" />
            <span className="text-2xl font-bold">
              {formatNumber(latest.maxWeightKg)} kg
            </span>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("strengthProgress:summary.bestOneRepMax")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <Trophy className="h-5 w-5 text-primary" />
              <span className="text-2xl font-bold">
                {formatNumber(best)} kg
              </span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("strengthProgress:summary.latestOneRepMax")}:{" "}
              {formatNumber(latest.estimatedOneRepMaxKg)} kg
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("strengthProgress:summary.latestVolume")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <span className="text-2xl font-bold">
              {formatNumber(latest.volumeKg)} kg
            </span>
            <p className="mt-1 text-sm text-muted-foreground">
              {latest.setCount} {t("strengthProgress:summary.sets")}
            </p>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader className="gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>{t("strengthProgress:chart.title")}</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("strengthProgress:chart.normalDescription")}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(metrics) as Metric[]).map(key => (
              <Button
                key={key}
                size="sm"
                type="button"
                variant={metric === key ? "default" : "outline"}
                aria-pressed={metric === key}
                onClick={() => setMetric(key)}
              >
                {t(`strengthProgress:chart.${metrics[key].key}`)}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart
              data={points}
              margin={{ top: 8, right: 20, left: 4, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                minTickGap={28}
              />
              <YAxis
                width={74}
                tickFormatter={value => `${formatNumber(Number(value))} kg`}
              />
              <Tooltip
                labelFormatter={formatDate}
                formatter={(value: number) => [
                  `${formatNumber(Number(value))} kg`,
                  t(`strengthProgress:chart.${metrics[metric].key}`),
                ]}
              />
              <Line
                type="monotone"
                dataKey={metric}
                stroke={metrics[metric].color}
                strokeWidth={2.5}
                activeDot={{ r: 6 }}
                dot={{ r: points.length === 1 ? 5 : 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
          {points.length === 1 && (
            <p className="mt-3 text-sm text-muted-foreground">
              {t("strengthProgress:chart.normalSinglePoint")}
            </p>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
